import { useCappedList } from "@/hooks/useCappedList";
import { FilterOptions } from "@/types/FilterOptions";
import { SearchFilterV4 } from "@/types/SearchFilter";
import { Button, buttonVariants } from "@components/ui/button";
import PageSentiments from "@components/SearchFilter/partials/PageSentiments";
import { ButtonGroup } from "@components/ui/button-group";
import { i18n } from "@lingui/core";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
} from "@components/ui/command";
import { SmileIcon, XIcon } from "lucide-react";
import { Trans, useLingui } from "@lingui/react/macro";
import { SENTIMENT } from "@/utils/sentimentOptions";

export default function Sentiments({
  options,
  onChange,
  value,
}: {
  onChange: (value: SearchFilterV4, clear?: boolean) => void;
  value: SearchFilterV4["sentiment"];
  options: FilterOptions["filter_sentiments"];
}) {
  const { t } = useLingui();

  // Helpers
  const formatCappedList = useCappedList();
  const isArray = Array.isArray(value);

  return (
    <ButtonGroup>
      <div
        className={buttonVariants({
          size: "xs",
          variant: "outline",
          className: "pointer-events-none",
        })}
      >
        <SmileIcon />
        <Trans>Sentiment</Trans>
      </div>
      <div
        className={buttonVariants({
          size: "xs",
          variant: "outline-muted",
          className: "pointer-events-none",
        })}
      >
        {isArray && value.length > 1 ? (
          <Trans>är någon av</Trans>
        ) : (
          <Trans>är</Trans>
        )}
      </div>
      <Popover>
        <PopoverTrigger asChild>
          <Button size="xs" variant="outline">
            {value
              ? isArray
                ? formatCappedList(
                    value.map((source) => i18n._(SENTIMENT[source].label)),
                    { cap: 2 }
                  )
                : i18n._(SENTIMENT[value].label)
              : "N/A"}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 p-0">
          <Command>
            <CommandInput placeholder={t`Sentiment`} />
            <CommandList>
              <CommandEmpty>
                <Trans>Inga filter hittades</Trans>
              </CommandEmpty>
              <PageSentiments
                value={value}
                onChange={onChange}
                options={options}
              />
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <Button
        size="icon-xs"
        variant="outline"
        type="button"
        onClick={() => {
          onChange({
            sentiment: undefined,
          });
        }}
      >
        <XIcon />
        <span className="sr-only">
          <Trans>Ta bort filter</Trans>
        </span>
      </Button>
    </ButtonGroup>
  );
}
